import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF } from '@react-three/drei';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import {
  applyArticulatedPose,
  createBox3D,
  type BodyHandle,
  type Box3D,
  type JointHandle,
  type World,
} from 'box3d-web';
import './PortCrane.css';

interface PortCraneState {
  ready: boolean;
  dropped: boolean;
  payloadY: number;
  payloadX: number;
  swing: number;
  jointError: number;
  trolleyX: number;
  trolleyY: number;
  attached: boolean;
  wind: boolean;
  antiSway: boolean;
  operation: 'READY' | 'ATTACHED' | 'IN TRANSIT' | 'ON DOCK';
  payloadPreset: PayloadPreset;
  payloadMassKg: number;
  ccd: boolean;
  ccdStatus: 'READY' | 'TEST IMPULSE APPLIED' | 'IMPACT OBSERVED';
  maxSwing: number;
  period: number;
  settling: string;
  debug: boolean;
  antiSwayBefore: number;
  antiSwayAfter: number;
}

declare global {
  interface Window {
    __portCraneState?: PortCraneState;
  }
}

interface CraneRuntime {
  world: World;
  trolley: BodyHandle;
  cable: BodyHandle[];
  cargo: BodyHandle;
  joints: JointHandle[];
  cargoReleased: boolean;
  trolleyTargetX: number;
  trolleyTargetY: number;
  trolleyDriveX: number;
  trolleyDriveY: number;
  windEnabled: boolean;
  antiSwayEnabled: boolean;
  ids: Int32Array;
  transforms: Float32Array;
  payloadDensity: number;
  ccdEnabled: boolean;
  maxSwing: number;
  releaseClock: number;
  lastPeakClock: number;
  peakCount: number;
  antiSwayBaseline: number;
  antiSwayAfter: number;
  ccdTestPending: boolean;
  disposed: boolean;
}

interface Telemetry {
  payloadY: number;
  payloadX: number;
  swing: number;
  jointError: number;
  trolleyX: number;
  trolleyY: number;
  maxSwing: number;
  period: number;
  settling: string;
  antiSwayBefore: number;
  antiSwayAfter: number;
  ccdStatus?: PortCraneState['ccdStatus'];
}

type PayloadPreset = 'LIGHT' | 'STANDARD' | 'HEAVY';

const PAYLOADS: Record<PayloadPreset, { label: string; density: number; massKg: number }> = {
  LIGHT: { label: 'Light', density: 2, massKg: 0.62 },
  STANDARD: { label: 'Standard', density: 4, massKg: 1.25 },
  HEAVY: { label: 'Heavy', density: 8, massKg: 2.50 },
};

const INITIAL = {
  trolley: [2.2, 7.2, 0] as const,
  cable: [6.65, 6.05, 5.45, 4.85],
  cargo: [2.2, 3.9, 0] as const,
};
const CARGO_JOINT_ANCHOR = [2.2, 4.57, 0] as const;

const modulePromise: Promise<Box3D> = createBox3D();

function createRuntime(box3d: Box3D, payloadDensity = PAYLOADS.STANDARD.density, ccdEnabled = true): CraneRuntime {
  const world = box3d.createWorld({ gravity: [0, -9.81, 0], enableSleep: false, enableContinuous: true });
  const ground = world.createBody({ type: 'static', position: [0, -0.1, 0] });
  world.addBox(ground, [8, 0.1, 5], { friction: 0.9 });
  const dockPallet = world.createBody({ type: 'static', position: [2.2, 0.15, 0] });
  world.addBox(dockPallet, [1.2, 0.15, 1.2], { friction: 0.9 });
  const targetPallet = world.createBody({ type: 'static', position: [4.15, 0.15, 0] });
  world.addBox(targetPallet, [1.2, 0.15, 1.2], { friction: 0.9 });
  const targetBaseContainer = world.createBody({ type: 'static', position: [4.15, 0.9, 0] });
  world.addBox(targetBaseContainer, [0.65, 0.6, 0.8], { friction: 0.85 });

  const trolley = world.createBody({ type: 'kinematic', position: INITIAL.trolley });
  world.addBox(trolley, [0.35, 0.2, 0.35], { density: 1, friction: 0.7 });
  const cable = INITIAL.cable.map((y) => {
    const body = world.createBody({ type: 'dynamic', position: [INITIAL.trolley[0], y, 0], linearDamping: 0.25, angularDamping: 0.35 });
    // Keep the payload-to-link mass ratio below 10:1. The original 220:1
    // ratio exposed visible solver stretch instead of believable cable load.
    world.addBox(body, [0.09, 0.28, 0.09], { density: 80, friction: 0.5 });
    return body;
  });
  const cargo = world.createBody({ type: 'dynamic', position: INITIAL.cargo, ccd: ccdEnabled, linearDamping: 0.08, angularDamping: 0.32 });
  world.addBox(cargo, [0.65, 0.6, 0.8], { density: payloadDensity, friction: 0.85, restitution: 0.05 });

  const joints: JointHandle[] = [];
  // This is the underside of the trolley. Spherical-joint anchors are given
  // in world space at creation and retained as local anchors thereafter.
  joints.push(world.createSphericalJoint(trolley, cable[0], { anchor: [2.2, 6.93, 0], coneLimit: Math.PI * 0.45 }));
  for (let i = 0; i < cable.length - 1; i += 1) {
    joints.push(world.createSphericalJoint(cable[i], cable[i + 1], { anchor: [2.2, (INITIAL.cable[i] + INITIAL.cable[i + 1]) / 2, 0], coneLimit: Math.PI * 0.35 }));
  }
  joints.push(world.createSphericalJoint(cable[cable.length - 1], cargo, { anchor: CARGO_JOINT_ANCHOR, coneLimit: Math.PI * 0.4 }));

  const bodies = [trolley, ...cable, cargo];
  return {
    world,
    trolley,
    cable,
    cargo,
    joints,
    cargoReleased: false,
    trolleyTargetX: INITIAL.trolley[0],
    trolleyTargetY: INITIAL.trolley[1],
    trolleyDriveX: INITIAL.trolley[0],
    trolleyDriveY: INITIAL.trolley[1],
    windEnabled: false,
    antiSwayEnabled: false,
    ids: Int32Array.from(bodies),
    transforms: new Float32Array(bodies.length * 7),
    payloadDensity,
    ccdEnabled,
    maxSwing: 0,
    releaseClock: 0,
    lastPeakClock: 0,
    peakCount: 0,
    antiSwayBaseline: 0,
    antiSwayAfter: 0,
    ccdTestPending: false,
    disposed: false,
  };
}

function initialPoses(runtime: CraneRuntime) {
  return [
    { body: runtime.trolley, position: INITIAL.trolley, rotation: [0, 0, 0, 1] as const },
    ...runtime.cable.map((body, i) => ({ body, position: [INITIAL.trolley[0], INITIAL.cable[i], 0] as const, rotation: [0, 0, 0, 1] as const })),
    { body: runtime.cargo, position: INITIAL.cargo, rotation: [0, 0, 0, 1] as const },
  ];
}

function dockAlignedPoses(runtime: CraneRuntime) {
  return [
    { body: runtime.trolley, position: [4.15, INITIAL.trolley[1], 0] as const, rotation: [0, 0, 0, 1] as const },
    ...runtime.cable.map((body, i) => ({ body, position: [4.15, INITIAL.cable[i], 0] as const, rotation: [0, 0, 0, 1] as const })),
    { body: runtime.cargo, position: [4.15, INITIAL.cargo[1], 0] as const, rotation: [0, 0, 0, 1] as const },
  ];
}

function StaticStsCrane() {
  const { scene } = useGLTF('/models/port-crane-sts.glb');
  useEffect(() => {
    scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.castShadow = true;
        object.receiveShadow = true;
      }
    });
  }, [scene]);
  return <primitive object={scene} />;
}

function DynamicTrolley({ bodyRef }: { bodyRef: (node: THREE.Group | null) => void }) {
  return (
    <group ref={bodyRef}>
      <mesh castShadow receiveShadow><boxGeometry args={[0.8, 0.22, 0.72]} /><meshStandardMaterial color="#e6a426" metalness={0.78} roughness={0.26} /></mesh>
      <mesh position={[0.12, -0.24, 0]} castShadow><boxGeometry args={[0.44, 0.32, 0.56]} /><meshStandardMaterial color="#172734" metalness={0.65} roughness={0.24} /></mesh>
      <mesh position={[0.12, -0.24, 0.286]}><boxGeometry args={[0.31, 0.2, 0.025]} /><meshStandardMaterial color="#6bbfd7" metalness={0.18} roughness={0.1} /></mesh>
      {[-0.24, 0.24].map((x) => [-0.25, 0.25].map((z) => (
        <mesh key={`${x}-${z}`} position={[x, 0.14, z]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.075, 0.075, 0.09, 12]} /><meshStandardMaterial color="#101820" metalness={0.9} roughness={0.28} /></mesh>
      )))}
    </group>
  );
}

function CableLink({ bodyRef }: { bodyRef: (node: THREE.Group | null) => void }) {
  const corners = [-1, 1];
  return (
    <group ref={bodyRef}>
      {corners.map((x) => corners.map((z) => <mesh key={`${x}-${z}`} position={[x * 0.12, 0, z * 0.12]} castShadow><cylinderGeometry args={[0.026, 0.026, 0.56, 8]} /><meshStandardMaterial color="#d2d8df" metalness={0.92} roughness={0.23} /></mesh>))}
      <mesh position={[0, 0.25, 0]}><sphereGeometry args={[0.105, 10, 8]} /><meshStandardMaterial color="#596673" metalness={0.9} roughness={0.25} /></mesh>
      <mesh position={[0, -0.25, 0]}><sphereGeometry args={[0.105, 10, 8]} /><meshStandardMaterial color="#596673" metalness={0.9} roughness={0.25} /></mesh>
    </group>
  );
}

function ContainerPayload({ bodyRef }: { bodyRef: (node: THREE.Group | null) => void }) {
  const corners = [-1, 1];
  return (
    <group ref={bodyRef}>
      <mesh castShadow receiveShadow><boxGeometry args={[1.3, 1.2, 1.6]} /><meshStandardMaterial color="#b63822" metalness={0.7} roughness={0.34} /></mesh>
      {corners.map((x) => corners.map((z) => (
        <mesh key={`${x}-${z}`} position={[x * 0.57, 0, z * 0.7]} castShadow><boxGeometry args={[0.085, 1.12, 0.085]} /><meshStandardMaterial color="#f0b235" metalness={0.72} roughness={0.25} /></mesh>
      )))}
      {[-0.38, -0.19, 0, 0.19, 0.38].map((x) => (
        <mesh key={x} position={[x, 0, 0.807]}><boxGeometry args={[0.045, 1.02, 0.028]} /><meshStandardMaterial color="#7e1e18" metalness={0.78} roughness={0.3} /></mesh>
      ))}
      <mesh position={[0, 0.61, 0]}><boxGeometry args={[1.36, 0.08, 1.66]} /><meshStandardMaterial color="#8f271d" metalness={0.74} roughness={0.28} /></mesh>
      <mesh position={[0, -0.61, 0]}><boxGeometry args={[1.36, 0.08, 1.66]} /><meshStandardMaterial color="#6f1d18" metalness={0.78} roughness={0.3} /></mesh>
      <group position={[0, 0.72, 0]}>
        <mesh><boxGeometry args={[1.42, 0.08, 1.72]} /><meshStandardMaterial color="#e2a934" metalness={0.75} roughness={0.25} /></mesh>
        {corners.map((x) => corners.map((z) => <mesh key={`lock-${x}-${z}`} position={[x * 0.57, -0.08, z * 0.7]}><cylinderGeometry args={[0.07, 0.07, 0.18, 10]} /><meshStandardMaterial color="#f7d16d" metalness={0.88} roughness={0.2} /></mesh>))}
      </group>
    </group>
  );
}

function TerminalEnvironment() {
  const stackColors = ['#d04b2d', '#2f719c', '#d59b2e', '#377b59'];
  return (
    <group>
      <group position={[-4.2, 0.72, 2.55]}>
        <mesh castShadow receiveShadow><boxGeometry args={[5.8, 1.25, 2.3]} /><meshStandardMaterial color="#1b3444" metalness={0.45} roughness={0.55} /></mesh>
        <mesh position={[0, 0.68, 0]} castShadow><boxGeometry args={[5.45, 0.12, 2.08]} /><meshStandardMaterial color="#b68b3b" metalness={0.35} roughness={0.7} /></mesh>
        <mesh position={[0, 0.22, -1.18]}><boxGeometry args={[5.2, 0.22, 0.08]} /><meshStandardMaterial color="#101d27" roughness={0.8} /></mesh>
        <mesh position={[-1.4, 0.9, 0]}><boxGeometry args={[1.2, 0.12, 1.95]} /><meshStandardMaterial color="#3c5361" roughness={0.75} /></mesh>
      </group>
      <group position={[4.15, 0.5, 2.1]}>
        {stackColors.map((color, index) => <mesh key={`stack-${index}`} position={[(index % 2) * 1.35 - 0.68, Math.floor(index / 2) * 1.18, 0]} castShadow><boxGeometry args={[1.2, 1.0, 1.55]} /><meshStandardMaterial color={color} metalness={0.55} roughness={0.38} /></mesh>)}
        <mesh position={[0, 0.6, -0.82]}><boxGeometry args={[2.75, 0.08, 0.08]} /><meshStandardMaterial color="#e2a934" metalness={0.7} roughness={0.3} /></mesh>
      </group>
      <group position={[4.15, 0.9, 0]}>
        <mesh castShadow receiveShadow><boxGeometry args={[1.3, 1.2, 1.6]} /><meshStandardMaterial color="#2f719c" metalness={0.58} roughness={0.36} /></mesh>
        {[-1, 1].map((x) => [-1, 1].map((z) => <mesh key={`target-lock-${x}-${z}`} position={[x * 0.57, 0, z * 0.7]}><boxGeometry args={[0.085, 1.12, 0.085]} /><meshStandardMaterial color="#e2a934" metalness={0.72} roughness={0.25} /></mesh>))}
      </group>
      <mesh position={[-4.2, -0.08, 4.15]} receiveShadow><boxGeometry args={[7.4, 0.06, 0.06]} /><meshStandardMaterial color="#66a9bf" metalness={0.2} roughness={0.7} /></mesh>
    </group>
  );
}

function updateTrolleyDrive(runtime: CraneRuntime, dt: number) {
  runtime.trolleyDriveX = THREE.MathUtils.clamp(
    runtime.trolleyDriveX + THREE.MathUtils.clamp(runtime.trolleyTargetX - runtime.trolleyDriveX, -0.85 * dt, 0.85 * dt),
    0.75,
    4.35,
  );
  runtime.trolleyDriveY = THREE.MathUtils.clamp(
    runtime.trolleyDriveY + THREE.MathUtils.clamp(runtime.trolleyTargetY - runtime.trolleyDriveY, -0.6 * dt, 0.6 * dt),
    6.55,
    8.05,
  );
}

function updateCraneTelemetry(
  runtime: CraneRuntime,
  dt: number,
  delta: number,
  xOffset: number,
  previousSwing: number,
  clock: { current: number },
  onTelemetry: (value: Telemetry) => void,
) {
  runtime.releaseClock += dt;
  runtime.maxSwing = Math.max(runtime.maxSwing, xOffset);
  if (previousSwing > xOffset && previousSwing > 0.04) {
    runtime.peakCount += 1;
    runtime.lastPeakClock = runtime.releaseClock;
  }
  if (runtime.antiSwayEnabled) runtime.antiSwayAfter = Math.max(runtime.antiSwayAfter, xOffset);
  else runtime.antiSwayBaseline = Math.max(runtime.antiSwayBaseline, xOffset);

  let ccdStatus: PortCraneState['ccdStatus'] | undefined;
  if (runtime.ccdTestPending && runtime.transforms[(runtime.ids.length - 1) * 7 + 1] <= 0.9) {
    runtime.ccdTestPending = false;
    ccdStatus = 'IMPACT OBSERVED';
  }
  clock.current += delta;
  if (clock.current > 0.1) {
    clock.current = 0;
    const x = runtime.transforms[(runtime.ids.length - 1) * 7];
    const period = runtime.peakCount > 1 && runtime.lastPeakClock > 0 ? runtime.releaseClock / runtime.peakCount : 0;
    onTelemetry({
      payloadY: runtime.transforms[(runtime.ids.length - 1) * 7 + 1],
      payloadX: x,
      swing: xOffset,
      jointError: Math.max(...runtime.joints.map((joint) => runtime.world.getJointAnchorSeparation(joint))),
      trolleyX: runtime.transforms[0],
      trolleyY: runtime.transforms[1],
      maxSwing: runtime.maxSwing,
      period,
      settling: runtime.maxSwing > 0.08 && xOffset < runtime.maxSwing * 0.2 ? 'SETTLING' : 'OSCILLATING',
      antiSwayBefore: runtime.antiSwayBaseline,
      antiSwayAfter: runtime.antiSwayAfter,
      ccdStatus,
    });
  }
  return xOffset;
}

function CraneScene({ runtime, onTelemetry, debug }: { runtime: CraneRuntime; onTelemetry: (value: Telemetry) => void; debug: boolean }) {
  const meshRefs = useRef<Array<THREE.Group | null>>([]);
  const clockRef = useRef(0);
  const forceClockRef = useRef(0);
  const previousSwingRef = useRef(0);
  useFrame((_, delta) => {
    if (runtime.disposed || runtime.ids.length === 0) return;
    const dt = Math.min(delta, 0.05);
    forceClockRef.current += dt;
    if (runtime.windEnabled && !runtime.cargoReleased) {
      const gust = Math.sin(forceClockRef.current * 1.7) * 0.45 + Math.sin(forceClockRef.current * 3.1) * 0.12;
      runtime.world.applyForce(runtime.cargo, [gust, 0, 0]);
    }
    if (runtime.antiSwayEnabled) {
      for (const body of [...runtime.cable, runtime.cargo]) {
        const velocity = runtime.world.getLinearVelocity(body);
        runtime.world.setLinearVelocity(body, [velocity.x * 0.82, velocity.y, velocity.z * 0.82]);
        const angular = runtime.world.getAngularVelocity(body);
        runtime.world.setAngularVelocity(body, [angular.x * 0.86, angular.y * 0.86, angular.z * 0.86]);
      }
    }
    updateTrolleyDrive(runtime, dt);
    runtime.world.setKinematicTarget(runtime.trolley, [runtime.trolleyDriveX, runtime.trolleyDriveY, 0], [0, 0, 0, 1], dt);
    runtime.world.step(dt, 6);
    runtime.world.readTransforms(runtime.ids, runtime.transforms);
    const cargoOffset = (runtime.ids.length - 1) * 7;
    runtime.world.setBodyTransform(runtime.cargo, [runtime.transforms[cargoOffset], runtime.transforms[cargoOffset + 1], runtime.transforms[cargoOffset + 2]], [0, 0, 0, 1]);
    runtime.world.setAngularVelocity(runtime.cargo, [0, 0, 0]);
    runtime.world.readTransforms(runtime.ids, runtime.transforms);
    for (let i = 0; i < meshRefs.current.length; i += 1) {
      const mesh = meshRefs.current[i];
      if (!mesh) continue;
      const offset = i * 7;
      mesh.position.set(runtime.transforms[offset], runtime.transforms[offset + 1], runtime.transforms[offset + 2]);
      mesh.quaternion.set(runtime.transforms[offset + 3], runtime.transforms[offset + 4], runtime.transforms[offset + 5], runtime.transforms[offset + 6]);
    }
    const xOffset = Math.abs(runtime.transforms[(runtime.ids.length - 1) * 7] - INITIAL.cargo[0]);
    previousSwingRef.current = updateCraneTelemetry(
      runtime,
      dt,
      delta,
      xOffset,
      previousSwingRef.current,
      clockRef,
      onTelemetry,
    );
  });

  return (
    <>
      <ambientLight intensity={0.55} />
      <directionalLight position={[4, 12, 6]} intensity={2.7} castShadow shadow-mapSize-width={2048} shadow-mapSize-height={2048} />
      <hemisphereLight intensity={0.4} color="#9ec5ff" groundColor="#161d25" />
      <group>
        <mesh position={[0, -0.22, 0]} receiveShadow><boxGeometry args={[18, 0.2, 10]} /><meshStandardMaterial color="#242b34" roughness={0.93} /></mesh>
        <mesh position={[-4.6, -0.1, 2.9]} receiveShadow><boxGeometry args={[7.4, 0.08, 2.5]} /><meshStandardMaterial color="#123a51" roughness={0.86} /></mesh>
        <mesh position={[2.2, 0.15, 0]} castShadow receiveShadow><boxGeometry args={[2.4, 0.3, 2.4]} /><meshStandardMaterial color="#976027" roughness={0.85} /></mesh>
        <mesh position={[4.15, 0.15, 0]} castShadow receiveShadow><boxGeometry args={[2.4, 0.3, 2.4]} /><meshStandardMaterial color="#976027" roughness={0.85} /></mesh>
      </group>
      <TerminalEnvironment />
      <Suspense fallback={null}><StaticStsCrane /></Suspense>
      <DynamicTrolley bodyRef={(node) => { meshRefs.current[0] = node; }} />
      {[1, 2, 3, 4].map((index) => <CableLink key={index} bodyRef={(node) => { meshRefs.current[index] = node; }} />)}
      <ContainerPayload bodyRef={(node) => { meshRefs.current[5] = node; }} />
      {debug && <group>
        {[0, 1, 2, 3, 4].map((index) => <mesh key={`joint-marker-${index}`} position={[INITIAL.trolley[0], index === 0 ? 6.93 : index === 4 ? 4.57 : INITIAL.cable[index - 1] - 0.3, 0]}><sphereGeometry args={[0.07, 8, 6]} /><meshBasicMaterial color="#f4c96b" /></mesh>)}
      </group>}
      <gridHelper args={[18, 36, '#3b4652', '#202733']} position={[0, -0.11, 0]} />
    </>
  );
}

export default function PortCrane({ onBack }: { onBack: () => void }) {
  const runtimeRef = useRef<CraneRuntime | null>(null);
  const [runtime, setRuntime] = useState<CraneRuntime | null>(null);
  const [state, setState] = useState<PortCraneState>({ ready: false, dropped: false, payloadY: INITIAL.cargo[1], payloadX: INITIAL.cargo[0], swing: 0, jointError: 0, trolleyX: INITIAL.trolley[0], trolleyY: INITIAL.trolley[1], attached: false, wind: false, antiSway: false, operation: 'READY', payloadPreset: 'STANDARD', payloadMassKg: PAYLOADS.STANDARD.massKg, ccd: true, ccdStatus: 'READY', maxSwing: 0, period: 0, settling: 'READY', debug: false, antiSwayBefore: 0, antiSwayAfter: 0 });

  useEffect(() => {
    let disposed = false;
    modulePromise.then((box3d) => {
      if (disposed) return;
      const next = createRuntime(box3d, PAYLOADS.STANDARD.density, true);
      runtimeRef.current = next;
      setRuntime(next);
      setState((current) => ({ ...current, ready: true }));
    });
    return () => {
      disposed = true;
      if (runtimeRef.current) runtimeRef.current.disposed = true;
      runtimeRef.current?.world.destroy();
      runtimeRef.current = null;
    };
  }, []);

  useEffect(() => { window.__portCraneState = state; }, [state]);

  const telemetry = useCallback((value: Telemetry) => setState((current) => {
    const { ccdStatus, ...numericTelemetry } = value;
    return { ...current, ...numericTelemetry, ...(ccdStatus ? { ccdStatus } : {}) };
  }), []);
  const rebuildRuntime = (payloadPreset: PayloadPreset, ccd: boolean) => {
    const active = runtimeRef.current;
    if (!active) return;
    modulePromise.then((box3d) => {
      if (runtimeRef.current !== active) return;
      active.disposed = true;
      setRuntime(null);
      active.world.destroy();
      const next = createRuntime(box3d, PAYLOADS[payloadPreset].density, ccd);
      runtimeRef.current = next;
      setRuntime(next);
      setState((current) => ({ ...current, ready: true, attached: true, dropped: false, operation: 'ATTACHED', payloadPreset, payloadMassKg: PAYLOADS[payloadPreset].massKg, ccd, ccdStatus: 'READY', payloadX: INITIAL.cargo[0], payloadY: INITIAL.cargo[1], maxSwing: 0, period: 0, settling: 'READY', antiSwayBefore: 0, antiSwayAfter: 0 }));
    });
  };
  const attach = () => {
    const active = runtimeRef.current;
    if (!active) return;
    try {
      if (active.cargoReleased) {
        applyArticulatedPose(active.world, initialPoses(active), { joints: active.joints, clearVelocities: true, maxJointAnchorSeparation: 0.01 });
        active.joints.push(active.world.createSphericalJoint(active.cable[active.cable.length - 1], active.cargo, { anchor: CARGO_JOINT_ANCHOR, coneLimit: Math.PI * 0.4 }));
        active.cargoReleased = false;
        active.trolleyTargetX = INITIAL.trolley[0];
        active.trolleyTargetY = INITIAL.trolley[1];
        active.trolleyDriveX = INITIAL.trolley[0];
        active.trolleyDriveY = INITIAL.trolley[1];
      }
      setState((current) => ({ ...current, dropped: false, attached: true, operation: 'ATTACHED', payloadX: INITIAL.cargo[0], payloadY: INITIAL.cargo[1], swing: 0 }));
    } catch {
      setState((current) => ({ ...current, jointError: Number.POSITIVE_INFINITY }));
    }
  };
  const pickup = () => {
    const active = runtimeRef.current;
    if (!active || active.cargoReleased) return;
    active.trolleyTargetY = Math.max(active.trolleyTargetY, 7.75);
    setState((current) => ({ ...current, operation: 'IN TRANSIT' }));
  };
  const releaseToDock = () => {
    const active = runtimeRef.current;
    if (!active || active.cargoReleased) return;
    try {
      const result = applyArticulatedPose(active.world, dockAlignedPoses(active), { joints: active.joints, clearVelocities: true, maxJointAnchorSeparation: 0.01 });
      if (result.maxSeparation > 0.01) throw new Error('dock pose separation');
      const cargoJoint = active.joints.pop();
      if (cargoJoint) active.world.destroyJoint(cargoJoint);
      active.cargoReleased = true;
      active.trolleyTargetX = 4.15;
      active.trolleyDriveX = 4.15;
      active.world.setBodyTransform(active.cargo, [4.15, INITIAL.cargo[1], 0], [0, 0, 0, 1]);
      active.world.setLinearVelocity(active.cargo, [0, 0, 0]);
      active.world.setAngularVelocity(active.cargo, [0, 0, 0]);
      setState((current) => ({ ...current, dropped: true, attached: false, operation: 'ON DOCK', payloadX: 4.15, payloadY: INITIAL.cargo[1] }));
    } catch {
      setState((current) => ({ ...current, jointError: Number.POSITIVE_INFINITY }));
    }
  };
  const toggleWind = () => {
    const active = runtimeRef.current;
    if (!active) return;
    active.windEnabled = !active.windEnabled;
    setState((current) => ({ ...current, wind: active.windEnabled }));
  };
  const toggleAntiSway = () => {
    const active = runtimeRef.current;
    if (!active) return;
    active.antiSwayEnabled = !active.antiSwayEnabled;
    setState((current) => ({ ...current, antiSway: active.antiSwayEnabled }));
  };
  const setPayloadPreset = (payloadPreset: PayloadPreset) => {
    if (!runtimeRef.current || state.payloadPreset === payloadPreset) return;
    rebuildRuntime(payloadPreset, state.ccd);
  };
  const toggleCcd = () => {
    if (!runtimeRef.current) return;
    rebuildRuntime(state.payloadPreset, !state.ccd);
  };
  const ccdTest = () => {
    const active = runtimeRef.current;
    if (!active) return;
    if (!active.cargoReleased) {
      const cargoJoint = active.joints.pop();
      if (cargoJoint) active.world.destroyJoint(cargoJoint);
      active.cargoReleased = true;
    }
    active.trolleyTargetX = INITIAL.trolley[0];
    active.trolleyDriveX = INITIAL.trolley[0];
    active.world.setBodyTransform(active.cargo, INITIAL.cargo, [0, 0, 0, 1]);
    active.world.setLinearVelocity(active.cargo, [0, -1.5, 0]);
    active.ccdTestPending = true;
    active.world.applyImpulseToCenter(active.cargo, [0, -8, 0]);
    setState((current) => ({ ...current, dropped: true, attached: false, operation: 'ON DOCK', ccdStatus: 'TEST IMPULSE APPLIED' }));
  };
  const toggleDebug = () => setState((current) => ({ ...current, debug: !current.debug }));
  const swing = () => {
    const active = runtimeRef.current;
    if (!active) return;
    active.world.applyImpulseToCenter(active.cargo, [4.5, 0, 0]);
    active.maxSwing = 0;
    active.releaseClock = 0;
    active.antiSwayBaseline = 0;
    active.antiSwayAfter = 0;
    setState((current) => ({ ...current, dropped: false, attached: true, operation: 'IN TRANSIT' }));
  };
  const moveTrolley = (delta: number) => {
    const active = runtimeRef.current;
    if (!active || active.cargoReleased) return;
    active.trolleyTargetX = THREE.MathUtils.clamp(active.trolleyTargetX + delta, 0.75, 4.35);
  };
  const hoist = (delta: number) => {
    const active = runtimeRef.current;
    if (!active || active.cargoReleased) return;
    active.trolleyTargetY = THREE.MathUtils.clamp(active.trolleyTargetY + delta, 6.55, 8.05);
  };
  const drop = () => {
    const active = runtimeRef.current;
    if (!active) return;
    if (!active.cargoReleased) {
      const cargoJoint = active.joints.pop();
      if (cargoJoint) active.world.destroyJoint(cargoJoint);
      active.cargoReleased = true;
    }
    active.world.applyImpulseToCenter(active.cargo, [0, -5, 0]);
    setState((current) => ({ ...current, dropped: true, attached: false, operation: 'ON DOCK' }));
  };
  const reset = () => {
    const active = runtimeRef.current;
    if (!active) return;
    try {
      const result = applyArticulatedPose(active.world, initialPoses(active), { joints: active.joints, clearVelocities: true, maxJointAnchorSeparation: 0.01 });
      if (active.cargoReleased) {
        active.joints.push(
          active.world.createSphericalJoint(active.cable[active.cable.length - 1], active.cargo, {
            anchor: CARGO_JOINT_ANCHOR,
            coneLimit: Math.PI * 0.4,
          }),
        );
        active.cargoReleased = false;
      }
      const jointError = Math.max(
        result.maxSeparation,
        ...active.joints.map((joint) => active.world.getJointAnchorSeparation(joint)),
      );
      active.trolleyTargetX = INITIAL.trolley[0];
      active.trolleyTargetY = INITIAL.trolley[1];
      active.trolleyDriveX = INITIAL.trolley[0];
      active.trolleyDriveY = INITIAL.trolley[1];
      active.windEnabled = false;
      active.antiSwayEnabled = false;
      setState((current) => ({ ...current, ready: true, dropped: false, payloadY: INITIAL.cargo[1], payloadX: INITIAL.cargo[0], swing: 0, jointError, trolleyX: INITIAL.trolley[0], trolleyY: INITIAL.trolley[1], attached: true, wind: false, antiSway: false, operation: 'ATTACHED', ccdStatus: 'READY', maxSwing: 0, period: 0, settling: 'READY', antiSwayBefore: 0, antiSwayAfter: 0 }));
    } catch {
      setState((current) => ({ ...current, jointError: Number.POSITIVE_INFINITY }));
    }
  };

  return (
    <div className="port-crane-page" data-testid="port-crane-page">
      <header className="port-crane-header">
        <button type="button" className="port-crane-back" onClick={onBack}>← Back</button>
        <div><span className="port-crane-kicker">BOX3D / PORT OPERATIONS</span><h1>Port Crane Rig</h1></div>
        <span className={`port-crane-ready ${state.ready ? 'ready' : ''}`} data-testid="port-crane-ready">{state.ready ? 'LIVE PHYSICS' : 'LOADING WASM'}</span>
      </header>
      <main className="port-crane-main">
        <section className="port-crane-stage">
          <Canvas shadows camera={{ position: [13, 9.5, 15], fov: 46 }} onCreated={({ gl }) => { gl.setClearColor('#0b1119'); }}>
            <CraneScene runtime={runtime ?? createPlaceholderRuntime()} onTelemetry={telemetry} debug={state.debug} />
            <OrbitControls enablePan={false} minDistance={7} maxDistance={23} target={[0.5, 4.8, 0]} />
          </Canvas>
          <div className="port-crane-hud" data-testid="port-crane-hud">
            <div><span>PAYLOAD ALT</span><strong>{state.payloadY.toFixed(2)} m</strong></div>
            <div><span>SWING</span><strong>{state.swing.toFixed(2)} m</strong></div>
            <div><span>JOINT MAX</span><strong>{Number.isFinite(state.jointError) ? `${(state.jointError * 1000).toFixed(1)} mm` : 'ERROR'}</strong></div>
            <div><span>OPERATION</span><strong>{state.operation}</strong></div>
            <div><span>MAX SWING</span><strong>{state.maxSwing.toFixed(2)} m</strong></div>
          </div>
        </section>
        <aside className="port-crane-panel">
          <span className="port-crane-kicker">LIVE TEST CONSOLE</span>
          <h2>Ship-to-shore run</h2>
          <p>Lock the spreader, lift from the vessel, travel to the dock stack, then release.</p>
          <div className="port-crane-controls">
            <div className="port-crane-control-group"><span>Payload preset · recreated with density</span><div className="port-crane-control-row port-crane-three"><button type="button" data-testid="port-crane-payload-light" className={state.payloadPreset === 'LIGHT' ? 'active' : ''} onClick={() => setPayloadPreset('LIGHT')} disabled={!state.ready}>Light · {PAYLOADS.LIGHT.massKg.toFixed(2)} kg</button><button type="button" data-testid="port-crane-payload-standard" className={state.payloadPreset === 'STANDARD' ? 'active' : ''} onClick={() => setPayloadPreset('STANDARD')} disabled={!state.ready}>Standard · {PAYLOADS.STANDARD.massKg.toFixed(2)} kg</button><button type="button" data-testid="port-crane-payload-heavy" className={state.payloadPreset === 'HEAVY' ? 'active' : ''} onClick={() => setPayloadPreset('HEAVY')} disabled={!state.ready}>Heavy · {PAYLOADS.HEAVY.massKg.toFixed(2)} kg</button></div></div>
            <div className="port-crane-control-group"><span>Container operation</span><div className="port-crane-control-row"><button type="button" data-testid="port-crane-attach" onClick={attach} disabled={!state.ready || state.attached}>Attach spreader</button><button type="button" data-testid="port-crane-pickup" onClick={pickup} disabled={!state.ready || !state.attached || state.dropped}>Pick up</button></div></div>
            <button type="button" data-testid="port-crane-release" onClick={releaseToDock} disabled={!state.ready || !state.attached}>Release to dock stack</button>
            <div className="port-crane-control-group"><span>Trolley travel</span><div className="port-crane-control-row"><button type="button" data-testid="port-crane-trolley-left" onClick={() => moveTrolley(-0.8)} disabled={!state.ready || state.dropped}>← Left</button><button type="button" data-testid="port-crane-trolley-right" onClick={() => moveTrolley(0.8)} disabled={!state.ready || state.dropped}>Right →</button></div></div>
            <div className="port-crane-control-group"><span>Hoist</span><div className="port-crane-control-row"><button type="button" data-testid="port-crane-hoist-up" onClick={() => hoist(0.55)} disabled={!state.ready || state.dropped}>↑ Raise</button><button type="button" data-testid="port-crane-hoist-down" onClick={() => hoist(-0.55)} disabled={!state.ready || state.dropped}>Lower ↓</button></div></div>
            <div className="port-crane-control-group"><span>Safety assist</span><div className="port-crane-control-row"><button type="button" data-testid="port-crane-wind" className={state.wind ? 'active' : ''} onClick={toggleWind} disabled={!state.ready}>Wind {state.wind ? 'ON' : 'OFF'}</button><button type="button" data-testid="port-crane-anti-sway" className={state.antiSway ? 'active' : ''} onClick={toggleAntiSway} disabled={!state.ready}>Anti-sway {state.antiSway ? 'ON' : 'OFF'}</button></div></div>
            <div className="port-crane-control-group"><span>Pendulum / CCD test</span><div className="port-crane-control-row"><button type="button" data-testid="port-crane-swing" onClick={swing} disabled={!state.ready || state.dropped}>Pendulum pulse</button><button type="button" data-testid="port-crane-ccd-test" onClick={ccdTest} disabled={!state.ready}>CCD impact test</button></div></div>
            <div className="port-crane-control-row"><button type="button" data-testid="port-crane-ccd-toggle" className={state.ccd ? 'active' : ''} onClick={toggleCcd} disabled={!state.ready}>CCD {state.ccd ? 'ON' : 'OFF'}</button><button type="button" data-testid="port-crane-debug" className={state.debug ? 'active' : ''} onClick={toggleDebug} disabled={!state.ready}>Cable debug {state.debug ? 'ON' : 'OFF'}</button></div>
            <button type="button" data-testid="port-crane-drop" onClick={drop} disabled={!state.ready}>Drop load</button>
            <button type="button" data-testid="port-crane-reset" onClick={reset} disabled={!state.ready}>Reset rig</button>
          </div>
          <dl className="port-crane-status">
            <div><dt>Payload</dt><dd>{state.dropped ? 'RELEASED' : 'SUSPENDED'}</dd></div>
            <div><dt>Spreader</dt><dd>{state.attached ? 'TWIST-LOCKED' : 'CLEAR'}</dd></div>
            <div><dt>Wind</dt><dd>{state.wind ? 'GUSTING' : 'CALM'}</dd></div>
            <div><dt>Anti-sway</dt><dd>{state.antiSway ? 'ASSISTING' : 'OFF'}</dd></div>
            <div><dt>Payload mass</dt><dd data-testid="port-crane-payload-mass">{PAYLOADS[state.payloadPreset].label} · {state.payloadMassKg.toFixed(2)} kg</dd></div>
            <div><dt>CCD</dt><dd data-testid="port-crane-ccd-status">{state.ccd ? 'ON' : 'OFF'} · {state.ccdStatus}</dd></div>
            <div><dt>Pendulum</dt><dd data-testid="port-crane-pendulum-telemetry">max {state.maxSwing.toFixed(2)} m · {state.period.toFixed(2)} s · {state.settling}</dd></div>
            <div><dt>Anti-sway A/B</dt><dd data-testid="port-crane-anti-sway-ab">before {state.antiSwayBefore.toFixed(2)} / after {state.antiSwayAfter.toFixed(2)} m</dd></div>
            <div><dt>Trolley</dt><dd>{state.trolleyX.toFixed(2)} m</dd></div>
            <div className={state.debug ? 'port-crane-debug-row' : ''}><dt>Cable</dt><dd data-testid="port-crane-cable-debug">4 links / 5 joints{state.debug ? ' · MARKERS ON' : ''} · {Number.isFinite(state.jointError) ? `${(state.jointError * 1000).toFixed(1)} mm max error` : 'ERROR'}</dd></div>
            <div className="port-crane-scorecard" data-testid="port-crane-scorecard"><dt>Reel scorecard</dt><dd>{state.operation} · {state.ccd ? 'CCD' : 'CCD OFF'} · {state.antiSway ? 'ASSISTED' : 'BASELINE'}</dd></div>
            <div><dt>Pose guard</dt><dd>{Number.isFinite(state.jointError) && state.jointError < 0.01 ? 'LOCKED' : 'CHECK'}</dd></div>
          </dl>
        </aside>
      </main>
    </div>
  );
}

function createPlaceholderRuntime(): CraneRuntime {
  // The scene is replaced once WASM is ready; this avoids rendering a blank
  // Canvas while the module loads without allocating a second physics world.
  const world = { step() {}, readTransforms() { return new Float32Array(); }, getJointAnchorSeparation() { return 0; } } as unknown as World;
  return {
    world,
    trolley: 0 as BodyHandle,
    cable: [],
    cargo: 0 as BodyHandle,
    joints: [],
    cargoReleased: false,
    trolleyTargetX: INITIAL.trolley[0],
    trolleyTargetY: INITIAL.trolley[1],
    trolleyDriveX: INITIAL.trolley[0],
    trolleyDriveY: INITIAL.trolley[1],
    windEnabled: false,
    antiSwayEnabled: false,
    ids: new Int32Array(),
    transforms: new Float32Array(),
    payloadDensity: PAYLOADS.STANDARD.density,
    ccdEnabled: true,
    maxSwing: 0,
    releaseClock: 0,
    lastPeakClock: 0,
    peakCount: 0,
    antiSwayBaseline: 0,
    antiSwayAfter: 0,
    ccdTestPending: false,
    disposed: false,
  };
}
